import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Row, Col, InputNumber, Button, Divider, Typography, Space, Table, message, Select } from 'antd'
import apiService from '../services/api'
import dayjs from 'dayjs'
import { isAndroid, isAndroidFunctionAvailable } from '../utils/androidUtils'

const { Title, Text } = Typography

const motivosCubagem = [
  { value: 'Re-cubagem (conferência)', label: 'Re-cubagem (conferência)' },
  { value: 'Erro no XML', label: 'Erro no XML' },
  { value: 'Ajuste manual', label: 'Ajuste manual' }
]

const formatWithComma = (value) => {
  if (value == null || value === '') return ''
  const n = typeof value === 'string' ? parseFloat(String(value).replace(',', '.')) : Number(value)
  if (Number.isNaN(n)) return ''
  const [int, dec] = n.toFixed(2).split('.')
  return `${String(int)},${dec}`
}

const parseWithComma = (value) => {
  if (value == null || value === '') return ''
  const s = String(value).replace(/\./g, '').replace(',', '.')
  const n = parseFloat(s)
  return Number.isNaN(n) ? '' : n
}

const formatM3 = (value) => {
  const n = Number(value)
  if (Number.isNaN(n)) return '0,000'
  return n.toFixed(3).replace('.', ',')
}

const decimalProps = {
  step: 0.01,
  stringMode: false,
  precision: 2,
  min: 0,
  formatter: formatWithComma,
  parser: parseWithComma,
}

const numberProps = {
  step: 1,
  stringMode: false,
  min: 0,
}

const LOCAL_CUBAGEM_PREFIX = 'nota-metragem:'
const loadLocalCubagem = (chave) => {
  try {
    const raw = localStorage.getItem(`${LOCAL_CUBAGEM_PREFIX}${chave}`)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed || null
  } catch (err) { console.warn('Falha ao carregar cubagem local', err); return null }
}
const saveLocalCubagem = (chave, data) => {
  try {
    localStorage.setItem(`${LOCAL_CUBAGEM_PREFIX}${chave}`, JSON.stringify(data))
    return true
  } catch (err) { console.warn('Falha ao salvar cubagem local', err); return false }
}

export default function NotaCubagemModal({ open, onClose, notaInfo, onSaved }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [items, setItems] = useState([])
  const [existingTotal, setExistingTotal] = useState(null)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null)
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null)
  const [focusAfterAddIdx, setFocusAfterAddIdx] = useState(null)
  const [notaMetragemDisabled, setNotaMetragemDisabled] = useState(false)
  const [motivo, setMotivo] = useState(undefined)

  const isAndroidDevice = isAndroid()

  const startMeasurement = () => {
    if (isAndroidFunctionAvailable('measureVolume')) {
      window.Android.measureVolume()
    } else {
      message.warning('Função de medição não disponível neste dispositivo')
    }
  }

  useEffect(() => {
    if (isAndroidDevice) {
      window.cubagem = (comprimento, largura, altura, volume) => {
        try {
          alert(`Volume calculado: ${volume} m³`)

          const newItem = {
            quantidade: 1,
            comprimento: Number(comprimento),
            largura: Number(largura),
            altura: Number(altura),
            total: Number(volume)
          }

          setItems(prev => [...prev, newItem])
          message.success('Medida adicionada com sucesso')
        } catch (err) {
          console.error('Erro ao processar cubagem do Android:', err)
          message.error('Erro ao processar medida')
        }
      }
    }

    return () => {
      if (typeof window !== 'undefined' && window.cubagem) {
        delete window.cubagem
      }
    }
  }, [isAndroidDevice])

  const chave = String(notaInfo?.chave || '')

  useEffect(() => {
    if (!open || !chave) return
    setLoading(true)
    if (notaMetragemDisabled) {
      const local = loadLocalCubagem(chave)
      if (local) {
        const it = Array.isArray(local?.items) ? local.items : []
        const mapped = it.map((r) => ({
          quantidade: Number(r.quantidade || 0),
          largura: Number(r.largura || 0),
          altura: Number(r.altura || 0),
          comprimento: Number(r.comprimento || 0),
          total: Number(r.total || 0),
        }))
        setItems(mapped)
        setExistingTotal(local?.totalM3 != null ? Number(local.totalM3) : (notaInfo && Number(notaInfo.m3)) || null)
        setLastUpdatedAt(local?.updatedAt || null)
        setLastUpdatedBy((local?.updatedBy && local.updatedBy.name) || null)
      } else {
        setItems([])
        setExistingTotal((notaInfo && Number(notaInfo.m3)) || null)
        setLastUpdatedAt(null)
        setLastUpdatedBy(null)
      }
      setLoading(false)
      return
    }

    apiService.notaMetragem.getByChave(chave)
      .then((res) => {
        const it = Array.isArray(res?.itens) ? res.itens : []
        const mapped = it.map((r) => ({
          quantidade: Number(r.quantidade || 0),
          largura: Number(r.largura || 0),
          altura: Number(r.altura || 0),
          comprimento: Number(r.comprimento || 0),
          total: Number(r.total || 0),
        }))
        setItems(mapped)
        setExistingTotal(res?.totalM3 != null ? Number(res.totalM3) : (notaInfo && Number(notaInfo.m3)) || null)
        setLastUpdatedAt(res?.updatedAt || null)
        setLastUpdatedBy((res?.updatedBy && res.updatedBy.name) || (res?.createdBy && res.createdBy.name) || null)
      })
      .catch(() => {
        setNotaMetragemDisabled(true)
        const local = loadLocalCubagem(chave)
        if (local) {
          const it = Array.isArray(local?.items) ? local.items : []
          const mapped = it.map((r) => ({
            quantidade: Number(r.quantidade || 0),
            largura: Number(r.largura || 0),
            altura: Number(r.altura || 0),
            comprimento: Number(r.comprimento || 0),
            total: Number(r.total || 0),
          }))
          setItems(mapped)
          setExistingTotal(local?.totalM3 != null ? Number(local.totalM3) : (notaInfo && Number(notaInfo.m3)) || null)
          setLastUpdatedAt(local?.updatedAt || null)
          setLastUpdatedBy((local?.updatedBy && local.updatedBy.name) || null)
        } else {
          setItems([])
          setExistingTotal((notaInfo && Number(notaInfo.m3)) || null)
          setLastUpdatedAt(null)
          setLastUpdatedBy(null)
        }
      })
      .finally(() => setLoading(false))
  }, [open, chave, notaMetragemDisabled])

  const subtotal = (row) => Number(row.largura || 0) * Number(row.altura || 0) * Number(row.comprimento || 0) * Number(row.quantidade || 0)
  const isPositiveNumber = (v) => typeof v === 'number' && !Number.isNaN(v) && v > 0
  const isRowComplete = (row) => isPositiveNumber(row.quantidade) && isPositiveNumber(row.largura) && isPositiveNumber(row.altura) && isPositiveNumber(row.comprimento)
  const totalM3 = useMemo(() => items.reduce((acc, r) => acc + subtotal(r), 0), [items])

  const updateRow = (idx, patch) => {
    setItems((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }
  const addRow = () => setItems((prev) => {
    const next = [...prev, { quantidade: null, largura: null, altura: null, comprimento: null }]
    setFocusAfterAddIdx(next.length - 1)
    return next
  })
  const removeRow = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx))

  const handleEnterFrom = (rowIdx, field) => (e) => {
    if (!e || e.key !== 'Enter') return
    e.preventDefault()
    e.stopPropagation()
    try {
      const nextIdMap = {
        qtd: `nc-largura-${rowIdx}`,
        largura: `nc-altura-${rowIdx}`,
        altura: `nc-comprimento-${rowIdx}`,
        comprimento: `nc-qtd-${rowIdx}`,
      }
      const nextId = nextIdMap[field]
      const el = document.getElementById(nextId)
      if (el) el.focus()
    } catch (err) { console.warn('Falha na navegação por ENTER', err) }
  }

  const finalizeRow = (rowIdx) => {
    try {
      const row = items[rowIdx]
      if (!isRowComplete(row)) {
        const order = ['quantidade', 'largura', 'altura', 'comprimento']
        const missing = order.find((k) => !isPositiveNumber(row[k]))
        if (missing) {
          const idMap = {
            quantidade: `nc-qtd-${rowIdx}`,
            largura: `nc-largura-${rowIdx}`,
            altura: `nc-altura-${rowIdx}`,
            comprimento: `nc-comprimento-${rowIdx}`,
          }
          const el = document.getElementById(idMap[missing])
          if (el) el.focus()
        }
        return
      }

      const q = Number(row.quantidade)
      const l = Number(Number(row.largura).toFixed(2))
      const a = Number(Number(row.altura).toFixed(2))
      const c = Number(Number(row.comprimento).toFixed(2))
      const sub = Number(q * l * a * c)

      let snapshotItems = []
      setItems((prev) => {
        const updated = prev.map((r, i) => (i === rowIdx ? { quantidade: q, largura: l, altura: a, comprimento: c, total: sub } : r))
        snapshotItems = updated
        const next = [...updated, { quantidade: null, largura: null, altura: null, comprimento: null }]
        setFocusAfterAddIdx(next.length - 1)
        return next
      })

      const totalSnap = snapshotItems.reduce((acc, r) => acc + subtotal(r), 0)
      const payload = {
        chave,
        totalM3: Number(totalSnap),
        items: snapshotItems.filter(isRowComplete).map((r) => ({
          quantidade: Number(r.quantidade || 0),
          largura: Number(r.largura || 0),
          altura: Number(r.altura || 0),
          comprimento: Number(r.comprimento || 0),
          total: Number(subtotal(r)),
        })),
        observacao: motivo || 'Alteração parcial de item na cubagem'
      }

      const doPersist = async () => {
        if (notaMetragemDisabled) {
          const localPayload = { ...payload, updatedAt: new Date().toISOString(), updatedBy: null }
          const ok = saveLocalCubagem(chave, localPayload)
          if (ok) {
            setExistingTotal(Number(totalSnap))
            setLastUpdatedAt(localPayload.updatedAt)
            setLastUpdatedBy(null)
            try { message.success('Item salvo localmente') } catch (e) { console.warn('Falha ao notificar sucesso local', e) }
          }
          return
        }
        try {
          const res = await apiService.notaMetragem.upsert(payload)
          setExistingTotal(Number(totalSnap))
          setLastUpdatedAt(res?.updatedAt || null)
          setLastUpdatedBy((res?.updatedBy && res.updatedBy.name) || (res?.createdBy && res.createdBy.name) || null)
          try { message.success('Item salvo') } catch (e) { console.warn('Falha ao notificar sucesso', e) }
        } catch (err) {
          const msg = String(err?.message || '').toLowerCase()
          const is404 = msg.includes('404') || msg.includes('not found')
          if (is404) {
            setNotaMetragemDisabled(true)
            const localPayload = { ...payload, updatedAt: new Date().toISOString(), updatedBy: null }
            const ok = saveLocalCubagem(chave, localPayload)
            if (ok) {
              setExistingTotal(Number(totalSnap))
              setLastUpdatedAt(localPayload.updatedAt)
              setLastUpdatedBy(null)
              try { message.warning('Endpoint indisponível. Item salvo localmente') } catch (e) { console.warn('Falha ao notificar aviso local', e) }
            }
          } else {
            try { message.error('Falha ao salvar item') } catch (e) { console.warn('Falha ao notificar erro', e) }
          }
        }
      }
      doPersist()
    } catch (err) { console.warn('Falha ao finalizar linha', err) }
  }

  useEffect(() => {
    if (focusAfterAddIdx == null) return
    const id = `nc-qtd-${focusAfterAddIdx}`
    const t = setTimeout(() => {
      try {
        const el = document.getElementById(id)
        if (el) el.focus()
      } catch (err) { console.warn('Falha ao focar input Qtd', err) }
      setFocusAfterAddIdx(null)
    }, 0)
    return () => clearTimeout(t)
  }, [focusAfterAddIdx])

  const handleSave = async () => {
    if (!chave) return
    if (!motivo) {
      message.warning('Por favor, selecione o motivo da alteração')
      return
    }
    setSaving(true)
    try {
      if (notaMetragemDisabled) {
        const payload = {
          chave,
          totalM3: Number(totalM3),
          items: items.filter(isRowComplete).map((r) => ({
            quantidade: Number(r.quantidade || 0),
            largura: Number(r.largura || 0),
            altura: Number(r.altura || 0),
            comprimento: Number(r.comprimento || 0),
            total: Number(subtotal(r)),
          })),
          updatedAt: new Date().toISOString(),
          updatedBy: null,
          observacao: motivo
        }
        const ok = saveLocalCubagem(chave, payload)
        if (ok) {
          message.warning('Endpoint indisponível. Cubagem salva localmente.')
          setExistingTotal(Number(totalM3))
          setLastUpdatedAt(payload.updatedAt)
          setLastUpdatedBy(null)
          try { onSaved && onSaved(payload) } catch (e) { console.warn('Falha ao notificar onSaved', e) }
          onClose()
        } else {
          message.error('Falha ao salvar cubagem local')
        }
        return
      }
      const payload = {
        chave,
        totalM3: Number(totalM3),
        items: items.filter(isRowComplete).map((r) => ({
          quantidade: Number(r.quantidade || 0),
          largura: Number(r.largura || 0),
          altura: Number(r.altura || 0),
          comprimento: Number(r.comprimento || 0),
          total: Number(subtotal(r)),
        })),
        observacao: motivo
      }
      const res = await apiService.notaMetragem.upsert(payload)
      message.success('Cubagem salva com sucesso')
      setExistingTotal(Number(totalM3))
      setLastUpdatedAt(res?.updatedAt || null)
      setLastUpdatedBy((res?.updatedBy && res.updatedBy.name) || (res?.createdBy && res.createdBy.name) || null)
      try { onSaved && onSaved(res) } catch (err) { console.warn('Falha ao notificar onSaved', err) }
      onClose()
    } catch (err) {
      const msg = String(err?.message || '').toLowerCase()
      const is404 = msg.includes('404') || msg.includes('not found')
      if (is404) {
        const payload = {
          chave,
          totalM3: Number(totalM3),
          items: items.filter(isRowComplete).map((r) => ({
            quantidade: Number(r.quantidade || 0),
            largura: Number(r.largura || 0),
            altura: Number(r.altura || 0),
            comprimento: Number(r.comprimento || 0),
            total: Number(subtotal(r)),
          })),
          updatedAt: new Date().toISOString(),
          updatedBy: null,
          observacao: motivo
        }
        const ok = saveLocalCubagem(chave, payload)
        if (ok) {
          message.warning('Endpoint indisponível. Cubagem salva localmente.')
          setExistingTotal(Number(totalM3))
          setLastUpdatedAt(payload.updatedAt)
          setLastUpdatedBy(null)
          try { onSaved && onSaved(payload) } catch (e) { console.warn('Falha ao notificar onSaved', e) }
          onClose()
        } else {
          message.error('Falha ao salvar cubagem local')
        }
      } else {
        message.error(String(err?.message || 'Falha ao salvar cubagem'))
      }
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (!notaMetragemDisabled || !chave) return
    const payload = {
      chave,
      totalM3: Number(totalM3),
      items: items.filter(isRowComplete).map((r) => ({
        quantidade: Number(r.quantidade || 0),
        largura: Number(r.largura || 0),
        altura: Number(r.altura || 0),
        comprimento: Number(r.comprimento || 0),
        total: Number(subtotal(r)),
      })),
      updatedAt: new Date().toISOString(),
      updatedBy: null,
    }
    saveLocalCubagem(chave, payload)
  }, [items, totalM3, notaMetragemDisabled, chave])

  const columns = [
    {
      title: 'Qtd',
      dataIndex: 'quantidade',
      width: 90,
      render: (_, row, idx) => (
        <InputNumber {...numberProps} id={`nc-qtd-${idx}`} value={row.quantidade} onChange={(v) => updateRow(idx, { quantidade: v })} autoFocus={focusAfterAddIdx === idx} onKeyDown={handleEnterFrom(idx, 'qtd')} />
      ),
    },
    {
      title: 'Largura (m)',
      dataIndex: 'largura',
      width: 140,
      render: (_, row, idx) => (
        <InputNumber
          {...decimalProps}
          id={`nc-largura-${idx}`}
          value={row.largura}
          placeholder="0,00"
          onChange={(v) => updateRow(idx, { largura: v })}
          onBlur={() => {
            const n = Number(row.largura)
            if (!Number.isNaN(n)) updateRow(idx, { largura: Number(n.toFixed(2)) })
          }}
          onKeyDown={handleEnterFrom(idx, 'largura')}
        />
      ),
    },
    {
      title: 'Altura (m)',
      dataIndex: 'altura',
      width: 140,
      render: (_, row, idx) => (
        <InputNumber
          {...decimalProps}
          id={`nc-altura-${idx}`}
          value={row.altura}
          placeholder="0,00"
          onChange={(v) => updateRow(idx, { altura: v })}
          onBlur={() => {
            const n = Number(row.altura)
            if (!Number.isNaN(n)) updateRow(idx, { altura: Number(n.toFixed(2)) })
          }}
          onKeyDown={handleEnterFrom(idx, 'altura')}
        />
      ),
    },
    {
      title: 'Comprimento (m)',
      dataIndex: 'comprimento',
      width: 160,
      render: (_, row, idx) => (
        <InputNumber
          {...decimalProps}
          id={`nc-comprimento-${idx}`}
          value={row.comprimento}
          placeholder="0,00"
          onChange={(v) => updateRow(idx, { comprimento: v })}
          onBlur={() => {
            const n = Number(row.comprimento)
            if (!Number.isNaN(n)) updateRow(idx, { comprimento: Number(n.toFixed(2)) })
          }}
          onKeyDown={(e) => { if (e && e.key === 'Enter') finalizeRow(idx) }}
        />
      ),
    },
    {
      title: 'Subtotal (m³)',
      dataIndex: 'total',
      width: 140,
      render: (_, row) => <Text>{isRowComplete(row) ? formatM3(subtotal(row)) : '-'}</Text>,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, __, idx) => (
        <Button danger type="link" onClick={() => removeRow(idx)}>Remover</Button>
      ),
    },
  ]

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
          <span>Cubagem da Nota</span>
          <Button
            type="primary"
            style={{
              backgroundColor: isAndroidDevice ? '#52c41a' : '#ff4d4f',
              borderColor: isAndroidDevice ? '#52c41a' : '#ff4d4f'
            }}
            onClick={startMeasurement}
            disabled={!isAndroidDevice}
          >
            Medir Cubagem
          </Button>
        </div>
      }
      onCancel={onClose}
      footer={null}
      width={typeof window !== 'undefined' && window.innerWidth <= 576 ? '95vw' : 720}
      centered
      maskClosable={false}
    >
      <div style={{ marginBottom: 12 }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} md={12}>
            <div style={{ borderLeft: '4px solid #1677ff', paddingLeft: 12 }}>
              <div><Text strong>Nota:</Text> <span>{String(notaInfo?.nota || '')}</span></div>
              <div><Text strong>Série:</Text> <span>{String(notaInfo?.serie || '')}</span></div>
              <div style={{ color: '#8c8c8c' }}><Text strong>Chave:</Text> <span title={chave}>{chave}</span></div>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ borderLeft: '4px solid #52c41a', paddingLeft: 12 }}>
              <div style={{ fontSize: 12, textAlign: 'center' }}>Destinatário</div>
              <div style={{ color: '#595959', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {String(notaInfo?.destinatario || '')}
              </div>
              <div style={{ marginTop: 6 }}>
                <Text strong>Total atual (m³): </Text>
                <span>{existingTotal != null ? formatM3(existingTotal) : '-'}</span>
              </div>
              <div style={{ marginTop: 4, color: '#8c8c8c' }}>
                <Text strong>Última atualização:</Text>
                <span> {lastUpdatedAt ? dayjs(lastUpdatedAt).format('DD/MM/YYYY HH:mm') : '-'}</span>
                {lastUpdatedBy ? <span> por {lastUpdatedBy}</span> : null}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Divider orientation="left">Itens de Cubagem</Divider>

      <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12}>
          <Space>
            <Button type="primary" onClick={addRow}>Adicionar Item</Button>
            <Button onClick={() => setItems([])}>Limpar</Button>
          </Space>
        </Col>
        <Col xs={24} sm={12}>
          <Select
            placeholder="Selecione o motivo da alteração"
            style={{ width: '100%' }}
            value={motivo}
            onChange={setMotivo}
            options={motivosCubagem}
          />
        </Col>
      </Row>

      <Table
        dataSource={items.map((r, i) => ({ key: i, ...r }))}
        columns={columns}
        pagination={false}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
      />

      <Divider />
      <Row justify="end" align="middle" gutter={[8, 8]}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>Total (m³): {formatM3(totalM3)}</Title>
        </Col>
        <Col>
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" onClick={handleSave} loading={saving}>Salvar</Button>
          </Space>
        </Col>
      </Row>
    </Modal>
  )
}